import { useCallback, useLayoutEffect, useRef } from 'react'

// Prevents Server side error
export const useUniversalLayoutEffect = typeof document === 'undefined' ? () => {} : useLayoutEffect

/**
 * Returns a stable ref that always points to the latest value.
 * @param value
 *
 * @example
 * const MyComponent = () => {
 *   const [count, setCount] = useState(0)
 *   const countRef = useLatestRef(count)
 *
 *   useEffect(() => {
 *     const interval = setInterval(() => {
 *       console.log(countRef.current)
 *     }, 1000)
 *     return () => clearInterval(interval)
 *   }, [])
 *
 *   return <button onClick={() => setCount(count + 1)}>Increment</button>
 * }
 *
 */
export function useLatestRef<T>(value: T): { current: T } {
  // Store the value in a ref
  const valueRef = useRef(value)

  // Update the ref to point to the new value on each render
  useUniversalLayoutEffect(() => {
    valueRef.current = value
  }, [value])

  // Return a stable ref
  return valueRef
}

/**
 * Returns a stable callback that will always call the latest version of the handler.
 * @param handler
 *
 * @example
 * const MyComponent = () => {
 *  const handleClick = useLatestCallbackRef(() => {
 *    console.log('Clicked!')
 *  })
 *  return <button onClick={handleClick}>Click me</button>
 * }
 */
export function useLatestCallbackRef<T extends (...args: any[]) => any>(handler: T): T {
  // Store the handler in a ref
  const handlerRef = useLatestRef(handler)

  // Return a stable function
  /*
      eslint-disable-next-line react-hooks/preserve-manual-memoization --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: `handlerRef` is used inside the callback but deps are empty.
      Possible fix approach:
        // Option 1: Add handlerRef to deps (it's stable so this is safe):
        return useCallback((...args: Parameters<T>) => {
          return handlerRef.current?.(...args)
        }, [handlerRef]) as T

        // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable):
        return ((...args: Parameters<T>) => {
          return handlerRef.current?.(...args)
        }) as T
  */
  return useCallback((...args: Parameters<T>) => {
    // Call the current handler with all arguments
    return handlerRef.current?.(...args)
  }, []) as T
}
